import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ShoppingBag, Search, Filter, Percent, Leaf, MapPin, 
  ExternalLink, Tag, Clock, Check, X, Loader2, Star,
  TrendingDown, Store, ShoppingCart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Supermarkets = () => {
  const [supermarkets, setSupermarkets] = useState([]);
  const [offers, setOffers] = useState([]);
  const [recommendedOffers, setRecommendedOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  
  // Filters
  const [selectedSupermarket, setSelectedSupermarket] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minDiscount, setMinDiscount] = useState('');
  const [bioOnly, setBioOnly] = useState(false);
  const [localOnly, setLocalOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSupermarkets();
    fetchOffers();
    fetchRecommendedOffers();
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [selectedSupermarket, selectedCategory, minDiscount, bioOnly, localOnly, searchQuery]);

  const fetchSupermarkets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supermarkets`);
      const data = await response.json();
      setSupermarkets(data.supermarkets || []);
    } catch (error) {
      console.error('Error fetching supermarkets:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSupermarket) params.append('supermarket_id', selectedSupermarket);
      if (selectedCategory) params.append('category', selectedCategory);
      if (minDiscount) params.append('min_discount', minDiscount);
      if (bioOnly) params.append('bio_only', 'true');
      if (localOnly) params.append('local_only', 'true');
      if (searchQuery) params.append('search', searchQuery);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/supermarkets/offers?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      setOffers(data.offers || []);
      setCategories(data.categories || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setLoading(false);
    }
  };

  const fetchRecommendedOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/supermarkets/offers/recommended`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      setRecommendedOffers(data.offers || []);
    } catch (error) {
      console.error('Error fetching recommended offers:', error);
    }
  };

  const clearFilters = () => {
    setSelectedSupermarket('');
    setSelectedCategory('');
    setMinDiscount('');
    setBioOnly(false);
    setLocalOnly(false);
    setSearchQuery('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short'
    });
  };

  const OfferCard = ({ offer }) => (
    <Card className="border-none card-shadow hover:shadow-lg transition-all duration-300 overflow-hidden">
      <CardContent className="p-0">
        {/* Discount Badge */}
        <div className="relative">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4" />
              <span className="font-bold text-lg">-{offer.discount_percent}%</span>
            </div>
            <div className="text-xs opacity-90">
              Până la {formatDate(offer.valid_until)}
            </div>
          </div>
          
          {/* Bio/Local badges */}
          <div className="absolute top-12 right-2 flex flex-col gap-1">
            {offer.is_bio && (
              <Badge className="bg-green-500 text-white text-xs">
                <Leaf className="w-3 h-3 mr-1" />
                BIO
              </Badge>
            )}
            {offer.is_local && (
              <Badge className="bg-amber-500 text-white text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                Local
              </Badge>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* Supermarket */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{offer.supermarket.logo}</span>
            <span className="text-sm text-[#4a5568]">{offer.supermarket.name}</span>
          </div>

          {/* Product name */}
          <h3 className="font-semibold text-[#1A4D2E] text-lg mb-1">
            {offer.product_name}
          </h3>
          
          <Badge variant="outline" className="text-xs mb-3">
            {offer.category}
          </Badge>

          {/* Prices */}
          <div className="flex items-end gap-3 mb-3">
            <span className="text-2xl font-bold text-[#1A4D2E]">
              {offer.discounted_price.toFixed(2)} RON
            </span>
            <span className="text-sm text-[#4a5568] line-through">
              {offer.original_price.toFixed(2)} RON
            </span>
            <span className="text-xs text-[#4a5568]">
              / {offer.unit}
            </span>
          </div>

          {/* Stock status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {offer.in_stock ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">În stoc</span>
                </>
              ) : (
                <>
                  <X className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-500">Stoc epuizat</span>
                </>
              )}
            </div>
            
            <a 
              href={offer.supermarket.website} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="sm" variant="outline" className="text-xs">
                <ExternalLink className="w-3 h-3 mr-1" />
                Vezi oferta
              </Button>
            </a>
          </div>

          {/* Match reason if recommended */}
          {offer.match_reason && (
            <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
              <p className="text-xs text-[#1A4D2E] flex items-center gap-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                {offer.match_reason}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-6 md:py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Oferte Supermarketuri
            </h1>
            <p className="text-[#4a5568] text-base md:text-lg">
              Găsește cele mai bune prețuri pentru alimentele tale sănătoase
            </p>
          </div>

          {/* Partner Supermarkets */}
          <Card className="border-none card-shadow mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#1A4D2E] text-lg flex items-center gap-2">
                <Store className="w-5 h-5" />
                Supermarketuri Partenere
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedSupermarket === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedSupermarket('')}
                  className={selectedSupermarket === '' ? 'bg-[#1A4D2E]' : ''}
                >
                  Toate
                </Button>
                {supermarkets.map((s) => (
                  <Button
                    key={s.id}
                    variant={selectedSupermarket === s.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedSupermarket(s.id)}
                    className={selectedSupermarket === s.id ? 'bg-[#1A4D2E]' : ''}
                  >
                    <span className="mr-1">{s.logo}</span>
                    {s.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="all" className="space-y-6">
            <TabsList className="bg-white p-1 rounded-xl">
              <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
                <Tag className="w-4 h-4 mr-2" />
                Toate Ofertele
              </TabsTrigger>
              <TabsTrigger value="recommended" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
                <Star className="w-4 h-4 mr-2" />
                Recomandate
              </TabsTrigger>
              <TabsTrigger value="best" className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white">
                <TrendingDown className="w-4 h-4 mr-2" />
                Cele Mai Bune
              </TabsTrigger>
            </TabsList>

            {/* All Offers Tab */}
            <TabsContent value="all">
              {/* Filters */}
              <Card className="border-none card-shadow mb-6">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-3 items-center">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4a5568]" />
                      <Input
                        placeholder="Caută produse..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#F5F5F0]"
                      />
                    </div>

                    {/* Category */}
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-sm"
                    >
                      <option value="">Toate categoriile</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>

                    {/* Min Discount */}
                    <select
                      value={minDiscount}
                      onChange={(e) => setMinDiscount(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-sm"
                    >
                      <option value="">Orice reducere</option>
                      <option value="10">Min 10%</option>
                      <option value="20">Min 20%</option>
                      <option value="30">Min 30%</option>
                      <option value="40">Min 40%</option>
                    </select>

                    {/* Bio Only */}
                    <Button
                      variant={bioOnly ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBioOnly(!bioOnly)}
                      className={bioOnly ? 'bg-green-600' : ''}
                    >
                      <Leaf className="w-4 h-4 mr-1" />
                      BIO
                    </Button>

                    {/* Local Only */}
                    <Button
                      variant={localOnly ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLocalOnly(!localOnly)}
                      className={localOnly ? 'bg-amber-600' : ''}
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      Local
                    </Button>

                    {/* Clear Filters */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-[#4a5568]"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Resetează
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Offers Grid */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
                </div>
              ) : offers.length > 0 ? (
                <>
                  <p className="text-sm text-[#4a5568] mb-4">
                    {offers.length} oferte găsite
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {offers.map((offer) => (
                      <OfferCard key={offer.id} offer={offer} />
                    ))}
                  </div>
                </>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="p-12 text-center">
                    <ShoppingBag className="w-12 h-12 text-[#4a5568] mx-auto mb-4" />
                    <h3 className="font-medium text-[#1A4D2E] mb-2">
                      Nicio ofertă găsită
                    </h3>
                    <p className="text-sm text-[#4a5568]">
                      Încearcă să ajustezi filtrele pentru mai multe rezultate
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Recommended Tab */}
            <TabsContent value="recommended">
              <Card className="border-none card-shadow mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1A4D2E]">Oferte Personalizate</h3>
                      <p className="text-sm text-[#4a5568]">
                        Bazate pe lista ta de cumpărături și preferințele alimentare
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {recommendedOffers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {recommendedOffers.map((offer) => (
                    <OfferCard key={offer.id} offer={offer} />
                  ))}
                </div>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="p-12 text-center">
                    <ShoppingCart className="w-12 h-12 text-[#4a5568] mx-auto mb-4" />
                    <h3 className="font-medium text-[#1A4D2E] mb-2">
                      Generează o listă de cumpărături
                    </h3>
                    <p className="text-sm text-[#4a5568]">
                      Vom recomanda oferte bazate pe ingredientele din lista ta
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Best Deals Tab */}
            <TabsContent value="best">
              <Card className="border-none card-shadow mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1A4D2E]">Cele Mai Mari Reduceri</h3>
                      <p className="text-sm text-[#4a5568]">
                        Ofertele cu discount de peste 30%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {offers
                  .filter(o => o.discount_percent >= 30)
                  .sort((a, b) => b.discount_percent - a.discount_percent)
                  .map((offer) => (
                    <OfferCard key={offer.id} offer={offer} />
                  ))}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default Supermarkets;
